"use client";

import React, {
  type ReactNode,
  useCallback,
  useSyncExternalStore,
} from "react";
import styles from "./OsPicker.module.css";

type OS = "Linux" | "Windows" | "macOS";

interface OsPickerProps {
  children: ReactNode;
  defaultOS?: OS;
}

interface OsPickerChildProps {
  children: ReactNode;
}

const OS_STORAGE_KEY = "fivem-docs-os-preference";
const VALID_OS_VALUES: OS[] = ["Linux", "Windows", "macOS"];

// Create a simple external store for OS preference
let listeners: Array<() => void> = [];

function emitChange() {
  for (const listener of listeners) {
    listener();
  }
}

function subscribeToOS(callback: () => void) {
  listeners = [...listeners, callback];
  // Also listen for storage events from other tabs
  const handleStorage = (e: StorageEvent) => {
    if (e.key === OS_STORAGE_KEY) {
      callback();
    }
  };
  window.addEventListener("storage", handleStorage);
  return () => {
    listeners = listeners.filter((l) => l !== callback);
    window.removeEventListener("storage", handleStorage);
  };
}

function getStoredOS(): OS | null {
  if (typeof window === "undefined") {
    return null;
  }
  const stored = localStorage.getItem(OS_STORAGE_KEY);
  if (stored && VALID_OS_VALUES.includes(stored as OS)) {
    return stored as OS;
  }
  return null;
}

function getServerSnapshot(): OS | null {
  return null;
}

export default function OsPicker({
  children,
  defaultOS = "Linux",
}: OsPickerProps) {
  const storedOS = useSyncExternalStore(
    subscribeToOS,
    getStoredOS,
    getServerSnapshot,
  );
  const activeOS = storedOS ?? defaultOS;

  const handleOSChange = useCallback((os: OS) => {
    localStorage.setItem(OS_STORAGE_KEY, os);
    emitChange();
  }, []);

  const osTabs: OS[] = ["Linux", "Windows", "macOS"];
  const childrenArray = React.Children.toArray(children);
  const activeChild = childrenArray.find((child) => {
    if (React.isValidElement(child)) {
      const childType = child.type as { displayName?: string };
      return childType.displayName === `OsPicker.${activeOS}`;
    }
    return false;
  });

  return (
    <div className={styles.osPicker}>
      <div
        aria-label="Operating System Selection"
        className={styles.osPickerTabs}
        role="tablist"
      >
        {osTabs.map((os) => (
          <button
            aria-controls={`os-panel-${os.toLowerCase()}`}
            aria-selected={activeOS === os}
            className={`${styles.osPickerTab} ${activeOS === os ? styles.osPickerTabActive : ""}`}
            id={`os-tab-${os.toLowerCase()}`}
            key={os}
            onClick={() => handleOSChange(os)}
            role="tab"
            type="button"
          >
            {os}
          </button>
        ))}
      </div>
      <div
        aria-labelledby={`os-tab-${activeOS.toLowerCase()}`}
        className={styles.osPickerContent}
        id={`os-panel-${activeOS.toLowerCase()}`}
        role="tabpanel"
      >
        {activeChild || null}
      </div>
    </div>
  );
}

export function OsPickerLinux({ children }: OsPickerChildProps) {
  return <>{children}</>;
}
OsPickerLinux.displayName = "OsPicker.Linux";

export function OsPickerWindows({ children }: OsPickerChildProps) {
  return <>{children}</>;
}
OsPickerWindows.displayName = "OsPicker.Windows";

export function OsPickerMacOS({ children }: OsPickerChildProps) {
  return <>{children}</>;
}
OsPickerMacOS.displayName = "OsPicker.macOS";

OsPicker.Linux = OsPickerLinux;
OsPicker.Windows = OsPickerWindows;
OsPicker.macOS = OsPickerMacOS;
